import React, { useEffect, useState } from 'react';

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const formatSize = (bytes) => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};

const formatDimensions = (img) => {
  if (!img?.width || !img?.height) return '';
  return `${img.width} × ${img.height}`;
};

const renderMeta = (img) => {
  if (!img) return '';

  const date = formatDate(img.createdTime);
  const size = formatSize(img.size);
  const dimensions =
    img.width && img.height ? `${img.width}×${img.height}` : '';

  return [date, size, dimensions].filter(Boolean).join(' • ');
};

const getImageSrc = (img) =>
  img?.fileLink ||
  img?.thumbnailLink ||
  img?.previewUrl ||
  img?.url ||
  '';

const getImageKey = (img, fallback = 0) =>
  img?.id || img?.fileId || img?.fileLink || fallback;

const getDownloadUrl = (img) => {
  const fileId = img?.id || img?.fileId;
  if (!fileId) return null;

  return `https://drive.google.com/uc?export=download&id=${fileId}`;
};

const openImage = (img) => {
  const link = img?.fileLink || img?.url;
  if (!link) return;
  window.open(link, '_blank', 'noopener,noreferrer');
};

const SimilarImagesPanel = ({
  isOpen,
  images,
  loading,
  selectedImage,
  onSelectImage,
  onClose
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!images.length) return;

    const selectedKey = getImageKey(selectedImage);
    const hasSelectedInsideGroup = selectedImage
      ? images.some((img, index) => getImageKey(img, index) === selectedKey)
      : false;

    if (!hasSelectedInsideGroup) {
      onSelectImage?.(images[0]);
    }
  }, [images, selectedImage, onSelectImage]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setCopied(false);
  }, [selectedImage]);

  const handleCopyPath = async () => {
    if (!selectedImage?.fullPath) return;

    try {
      await navigator.clipboard.writeText(selectedImage.fullPath);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      console.error('Не удалось скопировать путь', e);
    }
  };

  const handleDownload = () => {
    const downloadUrl = getDownloadUrl(selectedImage);

    if (!downloadUrl) {
      alert('Ссылка на скачивание недоступна');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Не удалось скачать изображение', e);
      alert('Не удалось скачать изображение');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.42)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1120px, 100%)',
          maxHeight: '90vh',
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: '#f3f4f6',
              borderRadius: '10px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            padding: '20px',
            overflow: 'auto'
          }}
        >
          {loading ? (
            <p>Загрузка...</p>
          ) : !images.length ? (
            <p>Нет изображений</p>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 320px',
                  gap: '20px',
                  marginBottom: images.length > 1 ? '20px' : '0'
                }}
              >
                <div
                  style={{
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <img
                    src={getImageSrc(selectedImage)}
                    alt={selectedImage?.title || 'selected'}
                    onClick={() => openImage(selectedImage)}
                    style={{
                      width: '100%',
                      maxHeight: '520px',
                      objectFit: 'contain',
                      background: '#fff',
                      display: 'block',
                      cursor: selectedImage?.fileLink ? 'pointer' : 'default'
                    }}
                    title="Открыть изображение"
                  />
                </div>

                <div
                  style={{
                    borderRadius: '14px',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    padding: '18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    alignSelf: 'start'
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>
                    Информация
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gap: '12px'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginBottom: '4px'
                        }}
                      >
                        Дата загрузки
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#111827',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '10px 12px'
                        }}
                      >
                        {formatDate(selectedImage?.createdTime) || '—'}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginBottom: '4px'
                        }}
                      >
                        Вес
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#111827',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '10px 12px'
                        }}
                      >
                        {formatSize(selectedImage?.size) || '—'}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginBottom: '4px'
                        }}
                      >
                        Масштаб
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#111827',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '10px 12px'
                        }}
                      >
                        {formatDimensions(selectedImage) || '—'}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginBottom: '4px'
                        }}
                      >
                        Путь
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px'
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            fontSize: '13px',
                            color: '#111827',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            padding: '10px 12px'
                          }}
                          title={selectedImage?.fullPath || ''}
                        >
                          {selectedImage?.fullPath || '—'}
                        </div>

                        {selectedImage?.fullPath && (
                          <button
                            onClick={handleCopyPath}
                            title={copied ? 'Скопировано' : 'Скопировать путь'}
                            style={{
                              width: '34px',
                              height: '34px',
                              minWidth: '34px',
                              borderRadius: '10px',
                              border: '1px solid #d1d5db',
                              background: copied ? '#eef6ff' : '#fff',
                              color: copied ? '#004DCF' : '#374151',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          >
                            <CopyIcon />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleDownload}
                    style={{
                      marginTop: '4px',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: 'none',
                      background: '#004DCF',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    Скачать изображение
                  </button>
                </div>
              </div>

              {images.length > 1 && (
                <div>
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#004DCF',
                      marginBottom: '12px'
                    }}
                  >
                    Похожие изображения
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: '12px'
                    }}
                  >
                    {images.map((img, index) => {
                      const imgKey = getImageKey(img, index);
                      const activeKey = getImageKey(selectedImage);
                      const isActive = activeKey === imgKey;

                      return (
                        <div
                          key={imgKey}
                          style={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: '#fff',
                            border: isActive
                              ? '2px solid #004DCF'
                              : '1px solid #e5e7eb',
                            boxShadow: isActive
                              ? '0 4px 16px rgba(0,77,207,0.12)'
                              : 'none'
                          }}
                        >
                          <div>
                            <img
                              src={getImageSrc(img)}
                              alt={img.title || 'similar'}
                              onClick={() => onSelectImage?.(img)}
                              style={{
                                width: '100%',
                                height: '130px',
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title="Выбрать изображение"
                            />
                          </div>

                          <div
                            style={{
                              padding: '6px 8px',
                              fontSize: '10px',
                              color: '#555',
                              lineHeight: 1.35,
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              borderTop: '1px solid #f1f1f1'
                            }}
                            title={renderMeta(img)}
                          >
                            {renderMeta(img)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimilarImagesPanel;